import React, { useEffect } from "react";
import clsx from "clsx";
import { makeStyles, Grid, Paper, Button } from "@material-ui/core";
import CommentIcon from '@material-ui/icons/Comment';
import StackBarChart from "Components/StackBarChart";
import Chart from "Components/Chart";
import DatePickers from "Components/DatePickers";
import DateSinglePicker from "Components/DateSinglePicker";
import DateTimePicker from "Components/DateTimePicker";

import { withRouter } from "react-router";

import Layout from "../../Layout";
import useAuth from "Hooks/useAuth";
import useApi from "Hooks/useApi";
import useFilter from "Hooks/useFilter";
import googleAnalytics from "fuction/googleAnalytics";
import {useQueryString} from "Hooks/useQueryString";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
  },
  fixedHeight: {
    height: 300,
  },
}));

function DashBoard(props) {
  const { request, convertQueryParam } = useApi();
  const auth = useAuth();
  const filter = useFilter();
  const target = props.target;
  const queryParams = useQueryString();

  const [mediaTypes, setMediaTypes] = React.useState([]);
  const [stackedBarChartData, setStackedBarChartData] = React.useState([]);
  const [lineChartData, setLineChartData] = React.useState([]);

  const fetchData = () => {
    // 戻るボタンやURL直貼りで遷移してきたとき、queryParams と auth.state の activeGroup が食い違っていても、
    // 初回レンダでは state の activeGroup （デフォルトグループ）でリクエストして返されたデータで画面表示されてしまう。
    // Layout.js で queryParams と auth.state の activeGroup の同期をとったあとで走る再レンダ時のリクエストで
    // 後からデータを取得しても画面に反映されない（set○○(●●)までで再々レンダは走らない）ので、
    // 同期前で食い違いがあるうちはリクエストしない。
    const activeMediaGroup = queryParams.get("activeMediaGroup");
    if (activeMediaGroup && auth.state.activeMediaGroup !== parseInt(activeMediaGroup, 10)) {
      return;
    }
    const params = {
      unit: props.unit,
      range: [props.fromDate, props.toDate],
    };

    // タイプ指定されていなければ棒グラフを用意
    if (!props.type || props.type == "null") {
      // 棒グラフ用多メディア情報取得
      request({
        method: "GET",
        url: `data/:account_id/media/counts/${
          auth.state.activeMediaGroup
        }?${convertQueryParam(params)}`,
      }).then((result) => {
        const responseData = result.data;
        let mediaData = [];
        // 棒グラフに利用できるようデータを編集
        responseData.map((data, index) => {
          let mediaObject = {};
          mediaObject[props.unit] = data[props.unit];

          // ラベル名: カウントに編集
          (data.count).map((count, index) => {
            mediaObject[count.label] = count.count;
          })

          mediaData.push(mediaObject);
        })

        setStackedBarChartData(mediaData);
      });
    }

    // メディア種別一覧取得
    request({
      method: "GET",
      url: `data/:account_id/media/types`,
    }).then(async (result) => {
      await setMediaTypes(result.data);
    });
  };

  React.useEffect(() => {
    googleAnalytics(props.location.pathname, auth.state.user.account_id);
  }, [props.location.pathname]);

  React.useEffect(() => {
    if (auth.state.activeMediaGroup === 0) {
      return;
    }

    fetchData();
  }, [auth.state.activeMediaGroup, filter.state, props.fromDate]);

  React.useEffect(async () => {
    if (Object.keys(mediaTypes).length == 0) {
      return;
    }

    setLineChartData([]);
    let mediaTypeData = [];

    // タイプ指定されていなければ全タイプの線グラフを用意
    if (!props.type || props.type == "null") {
      // メディア種別ごとの線グラフ用データを取得
      mediaTypeData = await Promise.all (mediaTypes.map(async (type, index) => {
        let data = {};

        const params = {
          type: type.id,
          unit: props.unit,
          range: [props.fromDate, props.toDate],
        };

        await request({
          method: "GET",
          url: `data/:account_id/media/counts/${
            auth.state.activeMediaGroup
          }?${convertQueryParam(params)}`,
        }).then((result) => {
          data.id = type.id;
          data.data = result.data;
        });
        return data;
      }));
    } else {
      const params = {
        type: props.type,
        unit: props.unit,
        range: [props.fromDate, props.toDate],
      };

      await request({
        method: "GET",
        url: `data/:account_id/media/counts/${
          auth.state.activeMediaGroup
        }?${convertQueryParam(params)}`,
      }).then((result) => {

        mediaTypeData.push({
          id: props.type,
          data: result.data,
        })
      })
    }
    setLineChartData(mediaTypeData);
  }, [mediaTypes]);

  const classes = useStyles();
  const fixedHeightPaper = clsx(classes.paper, classes.fixedHeight);

  const HeadDate = () => {
    if (target === "monthly") {
      return (
        <Grid
          container
          direction="row"
          alignItems="flex-end"
          justifyContent="flex-start"
        >
          <Grid item xs={9}>
            <DatePickers
              onChange={fetchData}
              mediaFlag={true}
            />
          </Grid>
          <Grid item xs={3}>
          </Grid>
        </Grid>
      );
    }
    if (target === "daily") {
      return (
        <DateSinglePicker
          date={props.fromDate}
          onChange={(date) => props.handleChangeDateSingle(date, props.type)}
          mediaFlag={true}
        />
      );
    }
    if (target === "hourly") {
      return (
        <DateTimePicker
          dateTime={props.fromDate}
          handleDateChange={(date) => props.handleDateChange(date, props.type)}
          step={60}
          mediaFlag={true}
        />
      );
    }
  };

  return (
    <Layout
      content={
        <Grid container spacing={3}>
          <HeadDate />
          {!props.type || props.type == "null" ? (
            <>
              <Grid item xs={12}>
                <Paper className={fixedHeightPaper}>
                  <StackBarChart
                    data={stackedBarChartData}
                    mediaTypes={mediaTypes}
                    target={target}
                    dataKey={props.unit}
                    onClick={(date) => props.onClickLineChart(date)}
                  />
                  <Grid container justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CommentIcon />}
                      onClick={() => props.onClickListButton(props.fromDate)}
                    >
                      投稿一覧
                    </Button>
                  </Grid>
                </Paper>
              </Grid>
              {mediaTypes.map((mediaType, index) => {
                let data = {};
                const filterData = lineChartData.filter((data) => {
                  return data.id == mediaType.id
                })

                if (filterData[0]) {
                  data = filterData[0].data;
                }

                return (
                  <Grid key={index} item xs={12}>
                    <Paper className={fixedHeightPaper}>
                      {mediaType.label}
                      <Chart
                        data={data}
                        dataKey={props.unit}
                        onClick={(date) => props.onClickLineChart(date, mediaType.id)}
                      />
                      <Grid container justifyContent="flex-end">
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<CommentIcon />}
                          onClick={() => props.onClickListButton(props.fromDate, mediaType.id)}
                        >
                          投稿一覧
                        </Button>
                      </Grid>
                    </Paper>
                  </Grid>
                )
              })}
            </>
          ) : (
            <>
              {mediaTypes.length > 0 && lineChartData.length > 0 && (
                <Grid item xs={12}>
                  <Paper className={fixedHeightPaper}>
                    {(mediaTypes.filter((mediaType) => {return mediaType.id == props.type}))[0].label}
                    <Chart
                      data={lineChartData[0].data}
                      dataKey={props.unit}
                      onClick={(date) => props.onClickLineChart(date, props.type)}
                    />
                    <Grid container justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CommentIcon />}
                        onClick={() => props.onClickListButton(props.fromDate, props.type)}
                      >
                        投稿一覧
                      </Button>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </>
          )}
        </Grid>
      }
    />
  );
}

export default withRouter(DashBoard);
